/**
 * t233u/t234u: which positions can be scheduled at which building. A position's `location_ids` empty (no rows) means
 * every building; otherwise only those buildings. Everything selected is stored as no rows, so "all" never needs a
 * count or badge. Pure: the API, the desktop settings, the shift forms and the phone share these rules.
 */
export interface PositionBuildingsLike { id: string; name?: string; archived_at?: string | null; location_ids?: readonly string[] | null }

export const POSITION_NOT_AT_BUILDING = 'position_not_at_building';

/** Allowed when there's no position, no restriction, or the building is in the list. */
export const positionAllowedAt = (p: Pick<PositionBuildingsLike, 'location_ids'> | null | undefined, locationId: string | null | undefined) =>
  !p || !p.location_ids?.length || !locationId || p.location_ids.includes(locationId);

export const isUnrestricted = (p: Pick<PositionBuildingsLike, 'location_ids'>) => !p.location_ids?.length;

/** Dedupes, keeps live buildings only, and stores everything selected as [] (no restriction). */
export function normalisePositionBuildings(ids: readonly string[] | null | undefined, live: readonly string[]): string[] {
  const set = new Set(live);
  const kept = [...new Set(ids ?? [])].filter(id => set.has(id)).sort();
  return !kept.length || live.every(id => kept.includes(id)) ? [] : kept;
}

/** Live positions that can be scheduled at a building, plus `keep` (a shift's current position) so editing never silently drops it. */
export function positionsAt<P extends PositionBuildingsLike>(positions: readonly P[], locationId: string | null | undefined, keep?: string | null): P[] {
  const live = positions.filter(p => !p.archived_at && positionAllowedAt(p, locationId));
  const k = keep && !live.some(p => p.id === keep) ? positions.find(p => p.id === keep) : undefined;
  return k ? [...live, k] : live;
}

/**
 * t477u: the rota's position axis (position view rows, the phone filter's fallback list) for one building: positions
 * allowed there (linked to it, or with no building rows), plus any position an existing shift at that building uses
 * so no shift is ever hidden. No building (or All buildings: pass null) keeps every position. Order is kept.
 */
export function axisPositions<P extends PositionBuildingsLike>(positions: readonly P[], locationId: string | null | undefined,
  shifts: readonly { position_id?: string | null; location_id?: string | null }[] = []): P[] {
  if (!locationId) return [...positions];
  const used = new Set(shifts.filter(s => s.position_id && (!s.location_id || s.location_id === locationId)).map(s => s.position_id as string));
  return positions.filter(p => positionAllowedAt(p, locationId) || used.has(p.id));
}

/** Buildings a position can be scheduled at (all of them without a position or a restriction), plus `keep`. */
export function buildingsFor<L extends { id: string }>(locations: readonly L[], p: Pick<PositionBuildingsLike, 'location_ids'> | null | undefined, keep?: string | null): L[] {
  return locations.filter(l => positionAllowedAt(p, l.id) || l.id === keep);
}

/** Short summary for settings: "All buildings", "Cromwell", "Cromwell, Vortex", "3 buildings". */
export function positionBuildingsLabel(p: Pick<PositionBuildingsLike, 'location_ids'>, names: ReadonlyMap<string, string>): string {
  const ids = p.location_ids ?? [];
  if (!ids.length) return 'All buildings';
  const n = ids.map(id => names.get(id)).filter(Boolean) as string[];
  return n.length <= 2 ? n.join(', ') : `${n.length} buildings`;
}

export type BuildingToggle =
  | { ok: true; location_ids: string[]; converted: boolean }
  | { ok: false; reason: 'last_building' };

/**
 * t234u: the building side. Adding a position here; removing it from here. Removing an unrestricted position turns it
 * into an explicit list of every other live building (`converted`, the UI confirms first). Removing a position from its
 * only building is refused: no rows means "everywhere", so it can't be expressed (archive the position instead).
 */
export function toggleBuildingPosition(p: Pick<PositionBuildingsLike, 'location_ids'>, locationId: string, on: boolean, live: readonly string[]): BuildingToggle {
  const cur = p.location_ids ?? [];
  if (on) {
    if (!cur.length || cur.includes(locationId)) return { ok: true, location_ids: [...cur], converted: false };
    return { ok: true, location_ids: normalisePositionBuildings([...cur, locationId], live), converted: false };
  }
  if (!cur.length) {
    const rest = live.filter(id => id !== locationId);
    if (!rest.length) return { ok: false, reason: 'last_building' };
    return { ok: true, location_ids: [...rest].sort(), converted: true };
  }
  if (!cur.includes(locationId)) return { ok: true, location_ids: [...cur], converted: false };
  const rest = cur.filter(id => id !== locationId);
  if (!rest.some(id => live.includes(id))) return { ok: false, reason: 'last_building' };
  return { ok: true, location_ids: normalisePositionBuildings(rest, live), converted: false };
}

/** Message for the API and the forms. */
export const positionNotAtBuildingMessage = (position: string, building: string) =>
  `${position} can't be scheduled at ${building}. Pick another position or building, or allow it in Settings > Positions.`;
